'use client';
import React, { useEffect, useRef } from 'react';
import AppImage from '@/components/ui/AppImage';

const steps = [
{
  num: '01',
  title: 'Book in 60 seconds',
  body: 'Pick your vehicle size, choose your service — interior detail from $99 or exterior wash from $49 — and grab a slot from live availability.',
  icon:
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <rect width="18" height="18" x="3" y="4" rx="2" ry="2" />
        <line x1="16" x2="16" y1="2" y2="6" /><line x1="8" x2="8" y1="2" y2="6" />
        <line x1="3" x2="21" y1="10" y2="10" />
        <path d="m9 16 2 2 4-4" />
      </svg>,

  img: "https://img.rocket.new/generatedImages/rocket_gen_img_17f3cc43d-1766845214545.png",
  imgAlt: 'Person booking on a smartphone in a cozy home office with morning light'
},
{
  num: '02',
  title: 'We come to you',
  body: 'Our fully equipped van arrives at your driveway within the 2-hour window. You don\'t lift a finger.',
  icon:
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M5 17H3a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11a2 2 0 0 1 2 2v3" />
        <rect width="13" height="9" x="9" y="11" rx="1" />
        <circle cx="12" cy="20" r="1" /><circle cx="19" cy="20" r="1" />
      </svg>,

  img: "https://images.unsplash.com/photo-1648542574207-e4625643adc1",
  imgAlt: 'Mobile detailing van parked in a quiet suburban driveway on a sunny afternoon'
},
{
  num: '03',
  title: 'Showroom result',
  body: 'Deep vacuum, steam clean, leather conditioning, odour removal — every pass leaves your car\'s interior looking and smelling brand new.',
  icon:
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <path d="m8 12 3 3 5-5" />
      </svg>,

  img: "https://images.unsplash.com/photo-1674596316712-79d6472eda4e",
  imgAlt: 'Perfectly polished black car hood reflecting a soft golden sunset with mirror-like clarity'
}];


export default function HowItWorks() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('visible');
        });
      },
      { threshold: 0.1 }
    );
    sectionRef?.current?.querySelectorAll('.reveal')?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <section id="how-it-works" className="py-24 px-6" ref={sectionRef}>
      <div
        className="max-w-6xl mx-auto rounded-5xl p-10 md:p-16 relative overflow-hidden noise-overlay"
        style={{
          background: 'linear-gradient(145deg, #000000 0%, #000000 50%, #000000 100%)'
        }}>
        
        {/* Background orb */}
        <div
          className="absolute top-0 right-0 w-96 h-96 rounded-full pointer-events-none"
          style={{
            background: 'radial-gradient(circle, rgba(59,130,246,0.12) 0%, transparent 70%)',
            filter: 'blur(60px)'
          }} />

        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-4">
            <span className="text-xs font-800 uppercase tracking-widest text-blue-400/70">How it works</span>
          </div>
          <h2 className="font-fraunces text-4xl md:text-5xl text-white leading-tight mb-14">
            Three steps to an interior<br />
            <span className="italic font-light text-blue-300">that turns heads.</span>
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {steps?.map((step, i) =>
            <div
              key={step?.num}
              className="reveal bento-card p-0 overflow-hidden"
              style={{
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(59,130,246,0.15)',
                transitionDelay: `${i * 0.12}s`
              }}>
              
                {/* Image */}
                <div className="relative h-48 overflow-hidden">
                  <AppImage
                  src={step?.img}
                  alt={step?.imgAlt}
                  fill
                  className="object-cover transition-transform duration-700 hover:scale-105" />
                
                  <div className="absolute inset-0 bg-gradient-to-t from-[#000000]/90 to-transparent" />
                  <span
                  className="absolute top-4 left-4 font-fraunces text-4xl font-700 text-white/20"
                  style={{ lineHeight: 1 }}>
                    {step?.num}
                  </span>
                </div>

                {/* Content */}
                <div className="p-6">
                  <div className="w-10 h-10 rounded-xl bg-blue-500/15 flex items-center justify-center text-blue-400 mb-4">
                    {step?.icon}
                  </div>
                  <h3 className="text-lg font-700 text-white mb-2">{step?.title}</h3>
                  <p className="text-sm text-white/50 leading-relaxed font-medium">{step?.body}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>);

}